import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { resolvePlaybackUrl, useHlsVideo } from '../../utils/hlsPlayer';

export const HookEditor = ({ video, axiosInstance, onSaved }) => {
  const initialStart = Number.isFinite(video.hook_start) ? video.hook_start : 20;
  const initialDuration = Number.isFinite(video.hook_duration) ? video.hook_duration : 30;
  const [start, setStart] = useState(initialStart);
  const [dur, setDur] = useState(initialDuration);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef(null);
  // Resolve the playable URL once — Bunny HLS or legacy file. Same helper the
  // main Player uses so behavior is consistent.
  const previewUrl = resolvePlaybackUrl(video);
  useHlsVideo(previewRef, previewing ? previewUrl : null, { autoStart: false });
  const trackLen = Number(video.duration || 0);
  const maxStart = trackLen > dur ? Math.max(0, Math.floor(trackLen - dur)) : 60;
  const dirty = Math.round(start) !== Math.round(initialStart) || Math.round(dur) !== Math.round(initialDuration);

  // Play exactly the selected hook window. Seek to `start`, play, then pause
  // when we cross `start + dur`. Keeps re-using the same <video> element so
  // hls.js doesn't have to re-attach between previews.
  const playHook = async () => {
    setPreviewing(true);
    // useHlsVideo attaches src on next render; wait one tick so the element
    // has a valid manifest before we seek.
    await new Promise((r) => setTimeout(r, 50));
    const v = previewRef.current;
    if (!v) return;
    try {
      v.currentTime = Math.max(0, Number(start));
      await v.play();
    } catch {
      toast.error("Couldn't start hook preview");
      setPreviewing(false);
    }
  };

  const stopHook = () => {
    const v = previewRef.current;
    if (v) {
      try { v.pause(); } catch { /* noop */ }
    }
    setPreviewing(false);
  };

  // Auto-stop at the end of the hook window. The 100ms tolerance avoids the
  // browser firing this listener mid-frame and skipping the pause.
  useEffect(() => {
    if (!previewing) return undefined;
    const v = previewRef.current;
    if (!v) return undefined;
    const limit = Number(start) + Number(dur);
    const onTime = () => {
      if (v.currentTime >= limit - 0.1) {
        try { v.pause(); } catch { /* noop */ }
        setPreviewing(false);
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [previewing, start, dur]);

  const fmt = (s) => {
    const sec = Math.max(0, Math.round(s));
    return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.patch(`/videos/${video.id}/hook?hook_start=${Math.round(start)}&hook_duration=${Math.round(dur)}`);
      toast.success(`Pulse hook set: ${fmt(start)}–${fmt(Number(start) + Number(dur))}`);
      if (onSaved) onSaved();
    } catch {
      toast.error('Failed to save hook');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/30" data-testid={`hook-editor-${video.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Pulse Hook</p>
        <span className="text-[11px] text-zinc-500 font-mono">
          {fmt(start)} → {fmt(Number(start) + Number(dur))}{trackLen ? ` of ${fmt(trackLen)}` : ''}
        </span>
      </div>
      <div className="space-y-2">
        <div>
          <label className="flex items-center justify-between text-[11px] text-zinc-400 mb-1">
            <span>Start at</span>
            <span className="font-mono">{fmt(start)}</span>
          </label>
          <input
            type="range"
            min={0}
            max={maxStart}
            step={1}
            value={start}
            onChange={(e) => setStart(Number(e.target.value))}
            className="w-full accent-white"
            data-testid={`hook-start-${video.id}`}
          />
        </div>
        <div>
          <label className="flex items-center justify-between text-[11px] text-zinc-400 mb-1">
            <span>Length</span>
            <span className="font-mono">{Math.round(dur)}s</span>
          </label>
          <input
            type="range"
            min={20}
            max={40}
            step={1}
            value={dur}
            onChange={(e) => setDur(Number(e.target.value))}
            className="w-full accent-white"
            data-testid={`hook-duration-${video.id}`}
          />
        </div>
      </div>
      {/* Inline preview — plays just the selected hook window so creators
          hear EXACTLY what Pulse will show before they save. Re-render when
          start/dur change to update which window plays next. */}
      {previewUrl && previewing && (
        <video
          ref={previewRef}
          className="mt-2 w-full aspect-video bg-black rounded-md"
          controls
          playsInline
          data-testid={`hook-preview-video-${video.id}`}
        />
      )}
      <button
        type="button"
        onClick={previewing ? stopHook : playHook}
        className={`mt-2 w-full text-xs font-semibold rounded-full py-1.5 ${
          previewing
            ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
            : 'bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200'
        }`}
        disabled={!previewUrl}
        data-testid={`hook-preview-btn-${video.id}`}
      >
        {previewing ? `■ Stop preview` : `▶ Play hook (${fmt(start)} → ${fmt(Number(start) + Number(dur))})`}
      </button>
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="mt-2 w-full text-xs font-semibold bg-white hover:bg-zinc-200 text-black rounded-full py-1.5 disabled:opacity-60"
          data-testid={`hook-save-${video.id}`}
        >
          {saving ? 'Saving…' : 'Save hook'}
        </button>
      )}
    </div>
  );
};
